import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from 'recharts';

const formatMoney = (amount) =>
  amount == null || amount === ''
    ? '—'
    : `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatPct = (value) =>
  value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`;

const formatQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

function KpiCard({ label, value, sub, accent = 'border-teal-500' }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${accent}`}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
      {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
    </div>
  );
}

function ChartCard({ title, subtitle, children, empty }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm h-full">
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {subtitle ? <p className="text-xs text-gray-500 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
      {empty ? (
        <p className="text-sm text-gray-500 py-16 text-center">Sin datos en el período.</p>
      ) : (
        children
      )}
    </div>
  );
}

function RankingTable({ rows, valueKey = 'margin', valueLabel = 'Margen' }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500 py-8 text-center">Sin datos en el período.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500 border-b">
            <th className="py-2 pr-2">#</th>
            <th className="py-2 pr-2">Producto</th>
            <th className="py-2 pr-2 text-right">Cant.</th>
            <th className="py-2 pr-2 text-right">P. unit.</th>
            <th className="py-2 pr-2 text-right">C. unit.</th>
            <th className="py-2 pr-2 text-right">Ventas</th>
            <th className="py-2 pr-2 text-right">Costo</th>
            <th className="py-2 text-right">{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.product_id || idx} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-2 pr-2 text-gray-400">{idx + 1}</td>
              <td className="py-2 pr-2">
                <div className="font-medium text-gray-800">{row.name}</div>
                {row.code ? <div className="text-xs font-mono text-gray-500">{row.code}</div> : null}
              </td>
              <td className="py-2 pr-2 text-right text-gray-600">{formatQty(row.quantity)}</td>
              <td className="py-2 pr-2 text-right text-gray-700">{formatMoney(row.avg_unit_price)}</td>
              <td className="py-2 pr-2 text-right text-gray-700">{formatMoney(row.avg_unit_cost)}</td>
              <td className="py-2 pr-2 text-right text-gray-700">{formatMoney(row.revenue)}</td>
              <td className="py-2 pr-2 text-right text-gray-700">{formatMoney(row.cost)}</td>
              <td className="py-2 text-right font-semibold text-teal-700">
                {valueKey === 'margin_pct'
                  ? formatPct(row.margin_pct)
                  : valueKey === 'quantity'
                    ? formatQty(row.quantity)
                    : formatMoney(row[valueKey])}
                {valueKey === 'margin' && row.margin_pct != null ? (
                  <div className="text-xs font-normal text-gray-500">{formatPct(row.margin_pct)}</div>
                ) : null}
                {(valueKey === 'revenue' || valueKey === 'quantity') && row.unit_margin != null ? (
                  <div className="text-xs font-normal text-gray-500">
                    Margen u. {formatMoney(row.unit_margin)}
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function VentasReportsDashboardView({ data, filteredByPos = false }) {
  if (!data) return null;

  const { kpis = {}, star_product: star, monthly_series: monthly = [] } = data;
  const chartMonthly = monthly.map((row) => ({
    ...row,
    name: row.label || row.period,
  }));

  const marginChart = (data.top_products_by_margin || []).slice(0, 8).map((row) => ({
    name: (row.name || '').slice(0, 18),
    margen: Number(row.margin || 0),
    ventas: Number(row.revenue || 0),
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Ventas netas"
          value={formatMoney(kpis.sales_net)}
          sub={`${kpis.orders_count || 0} ventas · Ticket ${formatMoney(kpis.avg_ticket)}`}
          accent="border-teal-500"
        />
        <KpiCard
          label="Costo mercadería"
          value={formatMoney(kpis.cogs)}
          sub={`Productos ${formatMoney(kpis.product_revenue)} · Servicios ${formatMoney(kpis.service_revenue)}`}
          accent="border-amber-500"
        />
        <KpiCard
          label="Margen bruto"
          value={formatMoney(kpis.margin)}
          sub={`Margen ${formatPct(kpis.margin_pct)}`}
          accent="border-emerald-500"
        />
        <KpiCard
          label="Producto estrella"
          value={star?.name ? star.name.slice(0, 28) : '—'}
          sub={
            star
              ? `${formatMoney(star.margin)} margen · ${formatPct(star.margin_pct)} · ${formatMoney(star.revenue)} ventas`
              : 'Sin productos con margen en el período'
          }
          accent="border-indigo-500"
        />
      </div>

      {kpis.products_without_cost > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Hay {kpis.products_without_cost} producto(s) vendido(s) sin costo de salida en kardex. El margen
          de esos ítems puede verse inflado hasta cerrar turno / generar salidas.
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="Ventas vs costo vs margen" subtitle="Serie mensual del período" empty={!chartMonthly.length}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartMonthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatMoney(value)} />
                <Legend />
                <Line type="monotone" dataKey="sales" name="Ventas" stroke="#0d9488" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="cost" name="Costo" stroke="#d97706" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="margin" name="Margen" stroke="#059669" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard
          title="Top productos por margen"
          subtitle="Los que más aportan al resultado"
          empty={!marginChart.length}
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={marginChart} layout="vertical" margin={{ left: 12, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatMoney(value)} />
                <Legend />
                <Bar dataKey="margen" name="Margen" fill="#0d9488" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="Ranking por margen S/" subtitle="Producto estrella y mayor contribución">
          <RankingTable rows={data.top_products_by_margin} valueKey="margin" valueLabel="Margen" />
        </ChartCard>
        <ChartCard title="Ranking por margen %" subtitle="Mejor rentabilidad relativa (con costo)">
          <RankingTable rows={data.top_products_by_margin_pct} valueKey="margin_pct" valueLabel="Margen %" />
        </ChartCard>
        <ChartCard title="Más vendidos por monto" subtitle="Mayor facturación neta">
          <RankingTable rows={data.top_products_by_revenue} valueKey="revenue" valueLabel="Ventas" />
        </ChartCard>
        <ChartCard title="Más vendidos por cantidad" subtitle="Volumen despachado / facturado">
          <RankingTable rows={data.top_products_by_quantity} valueKey="quantity" valueLabel="Cantidad" />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="Top clientes" subtitle="Por ventas netas">
          {!data.top_clients?.length ? (
            <p className="text-sm text-gray-500 py-8 text-center">Sin datos en el período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 border-b">
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Cliente</th>
                    <th className="py-2 text-right">Ventas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_clients.map((row, idx) => (
                    <tr key={row.client_id} className="border-b border-gray-100">
                      <td className="py-2 pr-3 text-gray-400">{idx + 1}</td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-800">{row.name}</div>
                        {row.code ? <div className="text-xs font-mono text-gray-500">{row.code}</div> : null}
                      </td>
                      <td className="py-2 text-right font-semibold text-teal-700">{formatMoney(row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Ventas por punto de venta" subtitle="Distribución del período">
          {filteredByPos ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              Elige &quot;Todos los POS&quot; en el filtro para ver el mix entre puntos de venta.
            </p>
          ) : !data.by_point_of_sale?.length ? (
            <p className="text-sm text-gray-500 py-8 text-center">Sin datos en el período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 border-b">
                    <th className="py-2 pr-3">POS</th>
                    <th className="py-2 pr-3 text-right">Ventas</th>
                    <th className="py-2 text-right"># docs</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_point_of_sale.map((row) => (
                    <tr key={row.point_of_sale_id} className="border-b border-gray-100">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-800">{row.name}</div>
                        {row.code ? <div className="text-xs font-mono text-gray-500">{row.code}</div> : null}
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold text-teal-700">{formatMoney(row.sales)}</td>
                      <td className="py-2 text-right text-gray-600">{row.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
